// Claim Intelligence Package™ — Report Metadata
//
// A small, immutable descriptor stamped onto every generated report so
// future intelligence improvements can be traced back to the exact
// version that produced the artifact. Stored inside `report_payload`
// (JSONB) as `report_metadata` — no schema migration required — and
// embedded into PDF document properties at export time.

export interface ReportMetadata {
  /** Overall Claim Intelligence Package™ contract version. */
  package_version: string;
  /** Version of the orchestrated intelligence engine. */
  intelligence_engine_version: string;
  /** Legal Intelligence dataset revision. */
  legal_dataset_version: string;
  /** Market Benchmark dataset revision. */
  market_dataset_version: string;
  /** OCR engine identifier and version. */
  ocr_engine: string;
  /** Confidence / scoring engine version. */
  confidence_engine_version: string;
  /** ISO-8601 UTC timestamp of generation. */
  generated_at: string;
  /** UUID that uniquely identifies this specific report artifact. */
  report_uuid: string;
}

/**
 * Central version registry. Bump values here when the corresponding
 * component evolves so historical reports remain traceable.
 */
export const REPORT_VERSIONS = {
  package_version: "1.0.0",
  intelligence_engine_version: "1.0.0",
  legal_dataset_version: "2026.07",
  market_dataset_version: "2026.07",
  ocr_engine: "openai-vision-1",
  confidence_engine_version: "1.0.0",
} as const;

function safeUUID(): string {
  try {
    const g = globalThis as unknown as { crypto?: Crypto };
    if (g.crypto && typeof g.crypto.randomUUID === "function") {
      return g.crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  // RFC 4122 v4 fallback for environments without crypto.randomUUID.
  const hex = "0123456789abcdef";
  let s = "";
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) s += "-";
    else if (i === 14) s += "4";
    else if (i === 19) s += hex[8 + Math.floor(Math.random() * 4)];
    else s += hex[Math.floor(Math.random() * 16)];
  }
  return s;
}

/** Build a fresh metadata block for a new report generation. */
export function buildReportMetadata(): ReportMetadata {
  return {
    ...REPORT_VERSIONS,
    generated_at: new Date().toISOString(),
    report_uuid: safeUUID(),
  };
}

/**
 * Extract a `ReportMetadata` from an arbitrary `report_payload` JSON blob,
 * returning `null` when the payload predates the metadata contract. Used
 * by the presentation layer and PDF engine — never mutates the input.
 */
export function readReportMetadata(
  payload: Record<string, unknown> | null | undefined,
): ReportMetadata | null {
  if (!payload || typeof payload !== "object") return null;
  const raw = (payload as { report_metadata?: unknown }).report_metadata;
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const str = (k: string) => (typeof r[k] === "string" ? (r[k] as string) : "");
  const uuid = str("report_uuid");
  const ts = str("generated_at");
  if (!uuid || !ts) return null;
  return {
    package_version: str("package_version") || REPORT_VERSIONS.package_version,
    intelligence_engine_version:
      str("intelligence_engine_version") || REPORT_VERSIONS.intelligence_engine_version,
    legal_dataset_version:
      str("legal_dataset_version") || REPORT_VERSIONS.legal_dataset_version,
    market_dataset_version:
      str("market_dataset_version") || REPORT_VERSIONS.market_dataset_version,
    ocr_engine: str("ocr_engine") || REPORT_VERSIONS.ocr_engine,
    confidence_engine_version:
      str("confidence_engine_version") || REPORT_VERSIONS.confidence_engine_version,
    generated_at: ts,
    report_uuid: uuid,
  };
}

/** Short version string safe to render in a footer or appendix line. */
export function shortVersionString(m: ReportMetadata): string {
  return `CIP v${m.package_version} · Engine ${m.intelligence_engine_version} · ${m.report_uuid.slice(0, 8)}`;
}
